import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/components/dialog';
import { Button } from '@repo/ui/components/button';
import {
  Info,
  Calendar,
  Users,
  Trophy,
  Tag,
  CheckCircle,
  MapPin,
  ClipboardList,
} from 'lucide-react';
import { DateFormat, formatDate } from '@/data/format/format-date';
import StatusBadge from './status-badge';
import { format } from 'date-fns';
import { DataTable } from '@repo/ui/components/data-table';
import { NotOkStatusIndicator, OkStatusIndicator } from '@repo/ui/components/status-indicator';

interface Reservation {
  id: string;
  phase: 'GROUP_PHASE' | 'ELIMINATION_PHASE';
  start?: string;
  end?: string;
  assigned?: boolean;
  court?: { id: string; name: string };
}

interface ExtendedRegistration extends Registration {
  timeInterests?: Array<{
    id: string;
    confirmed: boolean;
    courtReservation?: {
      id: string;
      start: string;
      end: string;
      court?: { name: string };
    };
  }>;
}

interface TournamentDetailsModalProps {
  tournament: Tournament & {
    eliminationPhaseStartDate?: string;
    eliminationPhaseStartTime?: string;
    reservations?: Reservation[];
    registrations?: ExtendedRegistration[];
  };
}

const columns = [
  {
    header: 'Pareja',
    cell: ({ row }: { row: { original: Registration } }) => {
      return `${row.original.playerOne} - ${row.original.playerTwo}`;
    },
  },
  {
    header: 'Categoría',
    accessorKey: 'categoryName',
    cell: ({ row }: { row: { original: Registration } }) => {
      return row.original.category?.name ?? '-';
    },
  },
  {
    header: 'Teléfono',
    accessorKey: 'phone',
    className: 'hidden lg:table-cell',
  },
  {
    header: 'Confirmado',
    accessorKey: 'confirmedDisplay',
    cell: ({ row }: { row: { original: Registration } }) => {
      return row.original.confirmed ? <OkStatusIndicator /> : <NotOkStatusIndicator />;
    },
  },
];

export function TournamentDetailsModal({ tournament }: TournamentDetailsModalProps) {
  const [open, setOpen] = useState(false);

  // Group phase reservations
  const groupPhaseReservations =
    tournament.reservations?.filter((r) => r.phase === 'GROUP_PHASE') ?? [];

  // Group reservations by court
  const reservationsByCourt = useMemo(() => {
    const grouped: Record<string, { courtName: string; reservations: Reservation[] }> = {};

    for (const reservation of groupPhaseReservations) {
      const courtId = reservation.court?.id ?? 'unknown';
      const courtName = reservation.court?.name ?? 'Cancha';

      if (!grouped[courtId]) {
        grouped[courtId] = { courtName, reservations: [] };
      }
      grouped[courtId].reservations.push(reservation);
    }

    // Sort reservations within each court by start time
    for (const courtId in grouped) {
      grouped[courtId].reservations.sort((a, b) => {
        if (!a.start || !b.start) return 0;
        return new Date(a.start).getTime() - new Date(b.start).getTime();
      });
    }

    return grouped;
  }, [groupPhaseReservations]);

  // Count registrations
  const totalRegistrations = tournament.registrations?.length ?? 0;
  const confirmedRegistrations = tournament.registrations?.filter((r) => r.confirmed)?.length ?? 0;

  // Estimated capacity (3 teams per window)
  const estimatedCapacity = groupPhaseReservations.length * 3;

  // Format time from date string
  const formatTime = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'HH:mm');
    } catch {
      return '--:--';
    }
  };

  // Format date from date string
  const formatDateShort = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'dd/MM');
    } catch {
      return '--/--';
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost">
          <Info className="w-4 h-4" />
          Detalles
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[95vw] max-w-[1200px]! h-[85vh] flex flex-col">
        <DialogHeader className="shrink-0 pb-4 border-b">
          <DialogTitle className="flex items-center gap-3 text-xl">
            <span>{tournament.name}</span>
            <StatusBadge status={tournament.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-8">
            {/* Header Cards - Stats Overview */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 text-blue-600 mb-2">
                  <Calendar className="w-5 h-5" />
                  <span className="text-sm font-medium">Fechas</span>
                </div>
                <p className="text-lg font-bold">
                  {formatDate(tournament.startDate, DateFormat.DD_MM_YYYY)}
                </p>
                <p className="text-sm text-muted-foreground">
                  al {formatDate(tournament.endDate, DateFormat.DD_MM_YYYY)}
                </p>
              </div>

              <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                <div className="flex items-center gap-2 text-purple-600 mb-2">
                  <Tag className="w-5 h-5" />
                  <span className="text-sm font-medium">Categorías</span>
                </div>
                <p className="text-3xl font-bold">{tournament.categories?.length ?? 0}</p>
              </div>

              <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                <div className="flex items-center gap-2 text-green-600 mb-2">
                  <Users className="w-5 h-5" />
                  <span className="text-sm font-medium">Inscripciones</span>
                </div>
                <p className="text-3xl font-bold">{totalRegistrations}</p>
                <p className="text-sm text-muted-foreground">
                  {confirmedRegistrations} confirmadas
                </p>
              </div>

              {tournament.mode === 'INSCRIPTION_ONLY' && groupPhaseReservations.length === 0 ? (
                <div className="p-4 bg-sky-50 rounded-xl border border-sky-100">
                  <div className="flex items-center gap-2 text-sky-600 mb-2">
                    <ClipboardList className="w-5 h-5" />
                    <span className="text-sm font-medium">Modo</span>
                  </div>
                  <p className="text-lg font-bold">Solo inscripcion</p>
                </div>
              ) : (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                  <div className="flex items-center gap-2 text-amber-600 mb-2">
                    <MapPin className="w-5 h-5" />
                    <span className="text-sm font-medium">Ventanas</span>
                  </div>
                  <p className="text-3xl font-bold">{groupPhaseReservations.length}</p>
                  <p className="text-sm text-muted-foreground">~{estimatedCapacity} parejas máx</p>
                </div>
              )}
            </div>

            {/* Two column layout for desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left column */}
              <div className="space-y-8">
                {/* Categories */}
                {tournament.categories && tournament.categories.length > 0 && (
                  <section className="space-y-4">
                    <h3 className="text-base font-semibold flex items-center gap-2 border-b pb-2">
                      <Tag className="w-5 h-5 text-purple-600" />
                      Categorías
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {tournament.categories.map((category) => {
                        const categoryCount =
                          tournament.registrations?.filter(
                            (r) =>
                              r.category?.id === category.id ||
                              (r as any).categoryId === category.id
                          )?.length ?? 0;
                        return (
                          <div
                            key={category.id}
                            className="p-3 bg-neutral-50 rounded-lg border flex items-center justify-between"
                          >
                            <span className="font-medium">{category.name}</span>
                            <span className="text-sm bg-neutral-200 px-2 py-1 rounded-full">
                              {categoryCount} parejas
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}

                {/* Elimination Phase */}
                {(tournament.eliminationPhaseStartDate || tournament.eliminationPhaseStartTime) && (
                  <section className="space-y-4">
                    <h3 className="text-base font-semibold flex items-center gap-2 border-b pb-2">
                      <Trophy className="w-5 h-5 text-amber-600" />
                      Fase de Eliminación
                    </h3>
                    <div className="p-5 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                      <div className="grid grid-cols-2 gap-6">
                        {tournament.eliminationPhaseStartDate && (
                          <div>
                            <p className="text-xs text-amber-700 uppercase tracking-wide mb-1">
                              Fecha de inicio
                            </p>
                            <p className="text-xl font-bold">
                              {formatDate(
                                tournament.eliminationPhaseStartDate,
                                DateFormat.DD_MM_YYYY
                              )}
                            </p>
                          </div>
                        )}
                        {tournament.eliminationPhaseStartTime && (
                          <div>
                            <p className="text-xs text-amber-700 uppercase tracking-wide mb-1">
                              Hora de inicio
                            </p>
                            <p className="text-xl font-bold">
                              {tournament.eliminationPhaseStartTime}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                )}
              </div>

              {/* Right column */}
              <div className="space-y-8">
                {/* Group Phase Windows - Grouped by Court */}
                {Object.keys(reservationsByCourt).length > 0 && (
                  <section className="space-y-4">
                    <h3 className="text-base font-semibold flex items-center gap-2 border-b pb-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      Ventanas de Fase de Grupos
                    </h3>
                    <div className="space-y-4">
                      {Object.entries(reservationsByCourt).map(
                        ([courtId, { courtName, reservations }]) => (
                          <div key={courtId} className="border rounded-xl overflow-hidden">
                            <div className="bg-blue-50 px-4 py-2 border-b border-blue-100">
                              <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                {courtName}
                                <span className="text-xs font-normal bg-blue-100 px-2 py-0.5 rounded-full ml-auto">
                                  {reservations.length} ventanas
                                </span>
                              </h4>
                            </div>
                            <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {reservations.map((reservation) => (
                                <div
                                  key={reservation.id}
                                  className={`p-2 rounded-lg text-center ${
                                    reservation.assigned
                                      ? 'bg-green-50 border border-green-200'
                                      : 'bg-white border border-neutral-200'
                                  }`}
                                >
                                  <p className="text-sm font-medium">
                                    {reservation.start
                                      ? formatDateShort(reservation.start)
                                      : '--/--'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {reservation.start && reservation.end
                                      ? `${formatTime(reservation.start)} - ${formatTime(
                                          reservation.end
                                        )}`
                                      : '--:-- - --:--'}
                                  </p>
                                  {reservation.assigned && (
                                    <div className="flex items-center justify-center gap-1 mt-1">
                                      <CheckCircle className="w-3 h-3 text-green-600" />
                                      <span className="text-xs text-green-600">Asignada</span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </section>
                )}
              </div>
            </div>

            {/* Registrations List - Full width */}
            {tournament.registrations && tournament.registrations.length > 0 && (
              <section className="space-y-4">
                <h3 className="text-base font-semibold flex items-center gap-2 border-b pb-2">
                  <Users className="w-5 h-5 text-green-600" />
                  Inscripciones ({totalRegistrations})
                </h3>
                <div className="max-h-80 overflow-y-auto">
                  <DataTable
                    total={tournament.registrations.length}
                    columns={columns}
                    data={tournament.registrations}
                  />
                </div>
              </section>
            )}

            {/* Empty State */}
            {!tournament.categories?.length &&
              !groupPhaseReservations.length &&
              !tournament.registrations?.length && (
                <div className="text-center py-12 text-muted-foreground">
                  <Info className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-lg">Este torneo aún no tiene configuración.</p>
                  <p className="text-sm">Agrega categorías y configura las fases para comenzar.</p>
                </div>
              )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
